import { MobileWaiverAPI } from '@kinderlabs-pos/apis/pos';
import {
	CartLineTicketUsageInfoType,
	OnlineTicketChannelType,
} from '@kinderlabs-pos/shared-data-type';
import { getUuidV4 } from '@kinderlabs-pos/shared-util';
import {
	GuestVisitState,
	OrderState,
	StoreInfoState,
	VaunceAppV2TicketsSearchState,
} from '@kinderlabs-pos/state';
import { CardStack, SimpleScrollbar } from '@kinderlabs-pos/ui-atoms';
import {
	FullSizeCircularProgress,
	RotatingRefreshButton,
	SearchBox,
	useConfirm,
} from '@kinderlabs-pos/ui-components';
import { Button, Checkbox, Chip, List, ListItemButton, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { VaunceAppTicketUserMemberInfoBox } from '../AppTicketUsageBoard/common/VaunceAppTicketUserMemberInfoBox';
import { CmtTicketVerificationModal, CmtVerificationResult } from './CmtTicketVerificationModal';
import { VaunceAppV2Api } from './lib/apis';
import { sendGAEvent } from './lib/posGtag';
import {
	getNotIssuableLabel,
	isTicketIssuable,
	MemberRootDetail,
	StoreVisitMemberWithTicketList,
	TicketDetailRoot,
	VaunceAppV2AppTicketTypeEnum,
} from './lib/types';

export * from './lib/apis';
export * from './lib/types';

export const 바운스오더QR코드찍으면있어야할데이터 = atom<
	Record<string, MemberRootDetail['storeVisitMemberList']> // guestVisitId
>({});

export const V2VaunceQrScanBoard = ({ handleClick결제 }: { handleClick결제: () => void }) => {
	const [search, setSearch] = useAtom(VaunceAppV2TicketsSearchState);

	const queryClient = useQueryClient();
	// 새로고침할 때마다 증가시켜, 같은 스캔이어도 정기권 본인 확인 모달을 다시 띄우게 한다.
	const [reloadCount, setReloadCount] = useState(0);
	const handleClickReload = async () => {
		await queryClient.invalidateQueries({ queryKey: ['vaunce-app-v2-integration', 'search', search] });
		setReloadCount((prev) => prev + 1);
	};

	return (
		<Stack
			height={'100%'}
			spacing={1}
			alignItems={'center'}
			pt={1}
			overflow={'auto'}>
			<Stack
				width={'100%'}
				spacing={1}
				direction={'row'}>
				<RotatingRefreshButton handleClick={handleClickReload} />
				<OnlineTicketSearchBox
					keyword={search}
					setSearch={setSearch}
				/>
			</Stack>
			<Typography
				variant={'body2'}
				textAlign={'center'}>
				{'바운스앱의 바운스오더 QR 코드를 스캔해주세요'}
			</Typography>
			<CardStack
				variant='nocolors'
				flex={1}
				spacing={1}
				overflow={'auto'}
				width={'100%'}
				height={'100%'}>
				<Suspense fallback={<FullSizeCircularProgress />}>
					<VaunceAppV2UsageBoardInternal
						search={search}
						reloadCount={reloadCount}
						handleClick결제={handleClick결제}
					/>
				</Suspense>
			</CardStack>
		</Stack>
	);
};

const OnlineTicketSearchBox = ({
	keyword,
	setSearch,
}: {
	keyword: string;
	setSearch: (keyword: string) => void;
}) => {
	const [tempSearchValue, setTempSearchValue] = useState(keyword);
	useEffect(() => {
		setTempSearchValue(keyword);
	}, [keyword]);

	return (
		<SearchBox
			value={tempSearchValue}
			onChange={(val) => {
				setTempSearchValue(val);
				// QR 을 지우면 검색 상태도 비워서, 같은 QR 을 다시 검색할 때 새 스캔으로 처리(본인 확인 모달 재노출)되게 한다.
				if (val === '') setSearch('');
			}}
			hasDeleteKey
			hasSearchButton
			onSearch={() => {
				setSearch(tempSearchValue);
			}}
			stackProps={{ width: '100%' }}
		/>
	);
};

const VaunceAppV2UsageBoardInternal = ({
	search,
	reloadCount,
	handleClick결제,
}: {
	search?: string;
	reloadCount: number;
	handleClick결제: () => void;
}) => {
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);
	const storeAndDevice = useAtomValue(StoreInfoState.curStoreAndDevice);
	const [message, setMessage] = useState<string | null>(null);
	const [cmtModalOpen, setCmtModalOpen] = useState(false);
	const [modalShownAt, setModalShownAt] = useState<string | null>(null);
	const [verificationCompleted, setVerificationCompleted] = useState(false);
	const [pendingTicketIds, setPendingTicketIds] = useState<Set<number>>(new Set());
	const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(new Set());
	// 같은 스캔(storeVisit)에 대해 모달을 이미 띄웠는지 추적한다.
	// refetch 에는 유지되고(재오픈 방지), remount(페이지 이탈 후 복귀)·새 스캔·새로고침 버튼에는 초기화된다.
	const handledScanIdRef = useRef<number | null>(null);
	const handledReloadCountRef = useRef(reloadCount);

	const { data, isLoading, isSuccess } = useQuery({
		queryKey: ['vaunce-app-v2-integration', 'search', search, storeInfo.id],
		queryFn: async () => {
			try {
				setMessage(null);

				if (!search) return null;
				const 티켓들 = await VaunceAppV2Api.scan방문등록ForTickets(search, storeInfo.id);
				const 방문 = await VaunceAppV2Api.scan방문등록ForGuestVisits(search);
				return {
					티켓들,
					방문,
				};
			} catch {
				setMessage('검색 결과가 없습니다.');
				return null;
			}
		},
		retry: false,
	});

	const cmtTickets = useMemo(
		() =>
			(data?.티켓들.storeVisitMemberWithTicketList ?? []).filter(
				(t): t is StoreVisitMemberWithTicketList & { ticketType: 'COMMUTATION' } =>
					t.ticketType === 'COMMUTATION' && t.ticketStatus === 'USABLE'
			),
		[data]
	);

	useEffect(() => {
		const scanId = data?.티켓들.id ?? null;
		if (cmtTickets.length > 0) {
			// 같은 스캔에 대한 refetch면 기존 인증 상태를 유지하고 모달을 다시 열지 않는다. (새로고침 버튼은 예외)
			if (handledScanIdRef.current === scanId && handledReloadCountRef.current === reloadCount) return;
			handledScanIdRef.current = scanId;
			handledReloadCountRef.current = reloadCount;

			setCmtModalOpen(true);
			setModalShownAt(dayjs().toISOString());
			setVerificationCompleted(false);
			setPendingTicketIds(new Set());
			setSelectedTicketIds(
				new Set(
					data?.티켓들.storeVisitMemberWithTicketList
						.filter((t) => t.ticketType !== 'COMMUTATION' && t.ticketStatus === 'USABLE')
						.map((t) => t.ticketId) ?? []
				)
			);
		} else {
			// 스캔 후 정기권이 다른 경로에서 먼저 사용되어(하루 1회 소진) USABLE 이 아니게 되면
			// cmtTickets 가 비는데, 이때 열려 있던 본인 확인 모달도 함께 닫아 멈춤 상태를 막는다.
			handledScanIdRef.current = null;
			setCmtModalOpen(false);
			setVerificationCompleted(true);
			setPendingTicketIds(new Set());
		}
	}, [cmtTickets, reloadCount]);

	const handleVerificationComplete = (results: CmtVerificationResult[]) => {
		const pendingIds = new Set(
			results.filter((r) => r.result === 'HOLD').map((r) => r.ticketId)
		);

		// 본인 확인 절차를 이벤트 로그로 남긴다 (정기권 1건당 1개 이벤트).
		results.forEach((r) => {
			sendGAEvent(
				'cmt_identity_verified',
				{
					center_id: String(storeInfo.id),
					pos_id: String(storeAndDevice.deviceId),
					bounce_order_id: String(data?.티켓들.id ?? ''),
					commutation_ticket_id: String(r.ticketId),
					modal_shown_at: modalShownAt ?? '',
					selected_at: r.selectedAt,
					verification_result: r.result,
				},
				{ clientId: `pos-${storeAndDevice.deviceId}` }
			);
		});

		setPendingTicketIds(pendingIds);
		// 본인 일치(= 보류가 아닌) 정기권은 기본 발권 대상으로 선택한다.
		setSelectedTicketIds((prev) => {
			const newSet = new Set(prev);
			cmtTickets.forEach((t) => {
				if (!pendingIds.has(t.ticketId)) newSet.add(t.ticketId);
			});
			return newSet;
		});
		setVerificationCompleted(true);
		setCmtModalOpen(false);
	};

	const tickets = useAtomValue(OrderState.cart.lines.ticketUsageSelector);
	const ticketMapByUsageId = useMemo(
		() =>
			tickets.reduce((acc, ticket) => {
				if (ticket.type === 'VAUNCE_APP_MUT') {
					acc[ticket.ticketUsageInfo.ticketId] = ticket;
				} else {
					acc[ticket.ticketUsageInfo.ticketId] = ticket;
				}
				return acc;
			}, {} as Record<string, CartLineTicketUsageInfoType>),
		[tickets]
	);

	const noContents = !data;

	useEffect(() => {
		if (data?.티켓들?.storeId && data.티켓들.storeId !== storeInfo.id) {
			setMessage('해당 매장의 방문등록이 아닙니다.');
		} else {
			setMessage(null);
		}
	}, [data, storeInfo.id]);

	const handleSelectTicket = (ticketId: number) => {
		setSelectedTicketIds((prev) => {
			const newSet = new Set(prev);
			if (prev.has(ticketId)) {
				newSet.delete(ticketId);
			} else {
				newSet.add(ticketId);
			}
			return newSet;
		});
	};

	return (
		<Stack sx={{ height: '100%' }}>
			{message && (
				<Stack
					height='100%'
					justifyContent={'center'}
					alignItems={'center'}>
					<Typography
						variant='subtitle1'
						color='text.secondary'>
						{message}
					</Typography>
				</Stack>
			)}
			{!noContents && !message && (
				<SimpleScrollbar>
					<Stack sx={{ height: '100%' }}>
						<VaunceAppTicketUserMemberInfoBox
							memberId={data.티켓들.storeVisitMainMemberInfo.memberId}
							userName={data.티켓들.storeVisitMainMemberInfo.username}
							userTelephone={data.티켓들.storeVisitMainMemberInfo.telephone}
						/>
						<List sx={{ flex: 1 }}>
							{data.티켓들.storeVisitMemberWithTicketList.map((ticket, idx) => (
								<ListItemButton
									disabled={!isTicketIssuable(ticket)}
									key={idx}
									sx={{ width: '100%', px: 0 }}>
									<Stack sx={{ width: '100%' }}>
										<Stack
											direction={'row'}
											sx={{ width: '100%', gap: 2, minWidth: 0 }}>
											{cmtTickets.length > 0 && (
												<Checkbox
													checked={
														ticket.ticketType === 'COMMUTATION'
															? selectedTicketIds.has(ticket.ticketId)
															: ticket.ticketStatus === 'USABLE'
													}
													readOnly={ticket.ticketType !== 'COMMUTATION'}
													onChange={() =>
														ticket.ticketType === 'COMMUTATION' &&
														handleSelectTicket(ticket.ticketId)
													}
												/>
											)}
											<Button
												variant='contained'
												sx={{ pointerEvents: 'none' }}>
												{VaunceAppV2AppTicketTypeEnum[ticket.ticketType]}
											</Button>
											<Stack sx={{ flex: 1, width: '100%', minWidth: 0 }}>
												<Typography
													variant='body1'
													sx={{
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
														minWidth: 0,
													}}>
													{ticket.ticketName}
													{ticket.ticketType === 'COMMUTATION' && `/${ticket.cmtUserInfo.username}`}
													{ticket.ticketType === 'MULTI_USE'
														? ` ${
																data.방문.storeVisitMemberList.filter(
																	(m) => m.ticketId === ticket.ticketId
																).length
														  }매 사용`
														: ''}
												</Typography>
												<Stack
													direction={'row'}
													sx={{ alignItems: 'center', gap: 1 }}>
													<Typography variant='body2'>{`${dayjs(ticket.issuedAt).format(
														'YYYY-MM-DD'
													)}  ~ ${dayjs(ticket.expiration).format('YYYY-MM-DD')}`}</Typography>
													{ticket.ticketType === 'MULTI_USE' && (
														<Typography variant='body1'>
															{ticket.mutChildren.filter((m) => !m.isUsed).length} /{' '}
															{ticket.mutChildren.length}
														</Typography>
													)}
												</Stack>

												<Typography variant='body2'>{ticket.isUsed}</Typography>
											</Stack>
											{ticket.ticketType === 'COMMUTATION' &&
												verificationCompleted &&
												isTicketIssuable(ticket) && (
												<Chip
													label={pendingTicketIds.has(ticket.ticketId) ? '보류' : '본인 확인'}
													color={pendingTicketIds.has(ticket.ticketId) ? 'default' : 'primary'}
													size='small'
													sx={{
														width: 'fit-content',
														height: 'auto',
													}}
												/>
											)}
											<Button
												variant='outlined'
												size='small'
												disabled={!!ticketMapByUsageId[`${ticket.ticketId}`]}
												sx={{ pointerEvents: 'none' }}>
												{!isTicketIssuable(ticket)
													? getNotIssuableLabel(ticket)
													: ticketMapByUsageId[`${ticket.ticketId}`]
													? '사용 중'
													: '사용가능'}
											</Button>
										</Stack>
										{ticket.ticketType === 'PACKAGE' && (
											<Stack sx={{ px: 2, py: 1 }}>
												{ticket.pkgItems.map((item, itemIdx) => (
													<Stack
														key={itemIdx}
														direction={'row'}
														sx={{
															alignItems: 'center',
															justifyContent: 'space-between',
															width: '100%',
														}}>
														<Typography variant='subtitle2'>{item.name}</Typography>
														<Typography variant='subtitle2'>
															{item.ticketType === 'NORMAL_PRODUCT' ? '상품' : '일회권'}
														</Typography>
													</Stack>
												))}
											</Stack>
										)}
									</Stack>
								</ListItemButton>
							))}
							{/* {Object.keys(onlineTicketGroupByOrderId).map((onlineTickerOrderId, idx) => {
							return (
								<OnlineTicketListGroupItem
									key={idx}
									onlineTicketsGroup={onlineTicketGroupByOrderId[onlineTickerOrderId]}
									defaultOpen={Object.values(onlineTicketGroupByOrderId).length < 3}
								/>
							);
						})} */}
						</List>
						<Stack
							direction={'row'}
							sx={{ gap: 2, py: 1 }}>
							{/* <등록고객응대버튼
								search={search ?? ''}
								data={data}
							/> */}
							<티켓일괄사용버튼
								data={data}
								selectedTicketIds={cmtTickets.length > 0 ? selectedTicketIds : undefined}
								qrcode={search ?? ''}
								ticketMapByUsageId={ticketMapByUsageId}
								handleClick결제={handleClick결제}
								verificationCompleted={verificationCompleted}
							/>
						</Stack>
						<CmtTicketVerificationModal
							open={cmtModalOpen && cmtTickets.length > 0}
							tickets={cmtTickets}
							onComplete={handleVerificationComplete}
						/>
					</Stack>
				</SimpleScrollbar>
			)}
		</Stack>
	);
};
// const 등록고객응대버튼 = ({ search, data }: { search: string; data?: TicketDetailRoot }) => {
// 	const orderDispatcher = useSetAtom(OrderState.value);

// 	const handleClick = async () => {
// 		console.log('aa');
// 		try {
// 			orderDispatcher({
// 				type: 'SET_GUEST_USERS_INFO',
// 				guestMemberInfo: {
// 					memberId: data.storeVisitMainMemberInfo.memberId,
// 					name: data.storeVisitMainMemberInfo.username,
// 					telephone: data.storeVisitMainMemberInfo.telephone,
// 					appUserId: data.storeVisitMainMemberInfo.memberId.toString(),
// 					users: [],
// 				},
// 			});
// 		} catch {}
// 	};

// 	return (
// 		<Button
// 			onClick={handleClick}
// 			sx={{ width: '100%' }}
// 			variant='outlined'>
// 			앱 등록 고객 응대
// 		</Button>
// 	);
// };

const 티켓일괄사용버튼 = ({
	data,
	selectedTicketIds,
	qrcode,
	ticketMapByUsageId,
	handleClick결제,
	verificationCompleted,
}: {
	data: {
		티켓들: TicketDetailRoot;
		방문: MemberRootDetail;
	};
	selectedTicketIds?: Set<number>;
	qrcode: string;
	ticketMapByUsageId: Record<string, CartLineTicketUsageInfoType>;
	handleClick결제: () => void;
	verificationCompleted: boolean;
}) => {
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);

	const confirm = useConfirm();
	const cartDispatch = OrderState.cart.useDispatcher();
	const orderDisptch = useSetAtom(OrderState.value);
	const rerunMutation = GuestVisitState.actions.useRerunMutation();
	const set바운스오더QR코드입력함 = useSetAtom(바운스오더QR코드찍으면있어야할데이터);
	// USABLE 이 아닌 티켓(환불/사용완료/만료 등)은 목록에는 보여주되 장바구니로는 넘기지 않는다.
	const issuableTickets = useMemo(
		() => data.티켓들.storeVisitMemberWithTicketList.filter(isTicketIssuable),
		[data]
	);
	const notIssuableCount = data.티켓들.storeVisitMemberWithTicketList.length - issuableTickets.length;

	const ticketToUse = useMemo(() => {
		return selectedTicketIds
			? issuableTickets.filter((t) => selectedTicketIds.has(t.ticketId))
			: issuableTickets;
	}, [issuableTickets, selectedTicketIds]);

	const handle티켓일괄사용 = () => {
		const confirmMessage =
			notIssuableCount > 0
				? `사용할 수 없는 티켓 ${notIssuableCount}개는 제외됩니다. 티켓 사용을 진행하시겠습니까?`
				: `티켓 사용을 진행하시겠습니까?`;
		confirm(confirmMessage, async () => {
			orderDisptch({ type: 'CLEAR' });

			const 방문등록정보 = (
				await MobileWaiverAPI.register({
					storeId: data.티켓들.storeId,
					vaunceRegisterId: qrcode,
				})
			).body;
			rerunMutation.mutate({ guestVisitId: 방문등록정보.id });

			set바운스오더QR코드입력함((prev) => ({
				...prev,
				[방문등록정보.id]: data.방문.storeVisitMemberList.filter((u) =>
					u.username.includes('보호자')
				),
			}));

			orderDisptch({
				type: 'SET_GUEST_USERS_INFO',
				guestMemberInfo: {
					...방문등록정보.memberInfo,
					sunbun: 방문등록정보.sunbun,
					users: 방문등록정보.users.filter((user) => !user.name?.includes('보호자')),
					guestVisitId: 방문등록정보.id,
					appUserId: `${방문등록정보.appMemberId}`,
				},
				appStoreVisitId: data.티켓들.id,
			});

			ticketToUse.forEach((ticket) => {
				if (!ticketMapByUsageId[`${ticket.ticketId}`]) {
					switch (ticket.ticketType) {
						case 'MULTI_USE': {
							data.방문.storeVisitMemberList
								.filter((member) => member.ticketType === 'MULTI_USE')
								.forEach((member) => {
									if (!member.mutChildId) return;
									const mutChildToUse = ticket.mutChildren
										.filter((mut) => !mut.isUsed)
										.find((m) => m.id === member.mutChildId);
									if (!mutChildToUse) return;

									cartDispatch({
										type: 'USAGES',
										subAction: {
											type: 'ADD_TICKET',
											cartLineId: getUuidV4(),
											ticketUsage: {
												type: 'MULTIUSE',
												useId: `${mutChildToUse.barcodeKey}`,
												name: ticket.ticketName,
												ticketId: ticket.ticketId,
												ticketInfoId: 0,
												userName: data.티켓들.storeVisitMainMemberInfo.username,
												userTelephone: data.티켓들.storeVisitMainMemberInfo.telephone,
												memberId: data.티켓들.storeVisitMainMemberInfo.memberId,
												ddiziInfo: [
													{
														name: ticket.ticketName,
														ddiziType: 'NORMAL',
														minutes:
															ticket.ticketMinutes ?? storeInfo.policy.defaultMinutesInCenter,
														hasNoAdditionalCharge: false,
														isNotForStatistics: false,
														available: 'ALL',
													},
												],
											},
										},
									});
								});

							break;
						}
						case 'ONE_TIME': {
							cartDispatch({
								type: 'USAGES',
								subAction: {
									type: 'ADD_TICKET',
									cartLineId: getUuidV4(),
									ticketUsage: {
										type: 'ONETIME',
										useId: `${ticket.ticketId}`,
										name: ticket.ticketName,
										ticketId: ticket.ticketId,
										ticketInfoId: 0,
										userName: data.티켓들.storeVisitMainMemberInfo.username,
										userTelephone: data.티켓들.storeVisitMainMemberInfo.telephone,
										memberId: data.티켓들.storeVisitMainMemberInfo.memberId,
										ddiziInfo: [
											{
												name: ticket.ticketName,
												ddiziType: ticket.ticketUser === 'PARENTS' ? 'PARENTS' : 'NORMAL',
												minutes: ticket.ticketMinutes ?? storeInfo.policy.defaultMinutesInCenter,
												hasNoAdditionalCharge: ticket.ticketUser === 'PARENTS',
												isNotForStatistics: false,
												available: 'ALL',
											},
										],
									},
								},
							});
							break;
						}
						case 'PACKAGE': {
							cartDispatch({
								type: 'USAGES',
								subAction: {
									type: 'ADD_TICKET',
									cartLineId: getUuidV4(),
									ticketUsage: {
										type: 'PACKAGE',
										useId: `${ticket.ticketId}`,
										name: ticket.ticketName,
										ticketId: ticket.ticketId,
										ticketInfoId: 0,
										userName: data.티켓들.storeVisitMainMemberInfo.username,
										userTelephone: data.티켓들.storeVisitMainMemberInfo.telephone,
										memberId: data.티켓들.storeVisitMainMemberInfo.memberId,
										ddiziInfo: ticket.pkgItems
											.filter((item) => item.ticketType === 'ONE_TIME')
											.map((item) => ({
												name: item.name,
												//@ts-ignore
												ddiziType: item.ticketUser === 'PARENTS' ? 'PARENTS' : 'NORMAL',
												//@ts-ignore
												minutes: item.ticketMinutes ?? storeInfo.policy.defaultMinutesInCenter,
												//@ts-ignore
												hasNoAdditionalCharge: item.ticketUser === 'PARENTS',
												available: 'ALL',
												isNotForStatistics: false,
											})),
									},
								},
							});
							break;
						}
						case 'COMMUTATION': {
							cartDispatch({
								type: 'USAGES',
								subAction: {
									type: 'ADD_TICKET',
									cartLineId: getUuidV4(),
									ticketUsage: {
										type: 'COMMUTATION',
										useId: `${ticket.ticketId}`,
										name: ticket.ticketName,
										ticketId: ticket.ticketId,
										ticketInfoId: 0,
										userName: data.티켓들.storeVisitMainMemberInfo.username,
										userTelephone: data.티켓들.storeVisitMainMemberInfo.telephone,
										memberId: data.티켓들.storeVisitMainMemberInfo.memberId,
										ddiziInfo: [
											{
												name: ticket.ticketName,
												ddiziType: 'NORMAL',
												minutes: ticket.ticketMinutes ?? storeInfo.policy.defaultMinutesInCenter,
												hasNoAdditionalCharge: false,
												isNotForStatistics: false,
												available: 'ALL',
											},
										],
									},
								},
							});
							break;
						}
						case 'ONLINE': {
							cartDispatch({
								type: 'ONLINE_TICKET_ONOFFMIX',
								subAction: {
									type: 'ADD',
									cartLineId: getUuidV4(),
									onlineTicketInfo: {
										id: 0,
										infoId: 0,
										ticketName: ticket.ticketName,
										purchased: ticket.issuedAt,
										expiration: ticket.expiration,
										status: 'USABLE',
										ticketId: ticket.onlineTicketDetailForPosAndKiosk.ticketId,
										ticketPrice: ticket.onlineTicketDetailForPosAndKiosk.ticketPrice,
										ticketType: ticket.ticketUser,
										klStoreId: storeInfo.id,
										ticketChannel: ticket.onlineTicketDetailForPosAndKiosk
											.ticketChannel as OnlineTicketChannelType,
										ticketProvider: 'ONOFFMIX',
										weekdayAvailable: ticket.onlineTicketDetailForPosAndKiosk.weekdayAvailable,
										externalTicketId: ticket.onlineTicketDetailForPosAndKiosk.onoffmixTicketId,
										externalOrderId: ticket.onlineTicketDetailForPosAndKiosk.onoffmixOrderId,
										externalTicketInfoId:
											ticket.onlineTicketDetailForPosAndKiosk.onoffmixTicketInfoId,
										ticketMinutes: ticket.onlineTicketDetailForPosAndKiosk.ticketMinutes,
										ddiziInfo: ticket.onlineTicketDetailForPosAndKiosk.ddiziInfo,
									},
								},
							});
							break;
						}
					}
				}

				orderDisptch({
					type: 'SET_VAUNCE_APP_V2',
					appStoreVisitId: data.티켓들.id,
				});
			});

			handleClick결제();
		});
	};

	return (
		<Button
			sx={{ width: '100%' }}
			variant='contained'
			disabled={!verificationCompleted || ticketToUse.length === 0}
			onClick={handle티켓일괄사용}>
			{selectedTicketIds
				? ticketToUse.length === issuableTickets.length
					? '전체 티켓 사용'
					: `${ticketToUse.length}개 / ${issuableTickets.length}개 티켓 사용`
				: '티켓 일괄 사용'}
		</Button>
	);
};
